import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getInventory,
  getUnopenedPoolOffers,
  listPoolShippings,
  sellPoolItem,
  sellInventoryItems,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
  type IframeUnopenedOffer,
} from '../../modules/api';
import { useInfinitePagination } from '../../modules/pagination';
import { useWallet } from '../../modules/wallet';
import { notifyBuyback } from '../../modules/notifications';

const PER_PAGE = 30;

/**
 * Packs / Cards / Deliveries feeds plus the Cards-tab selection and buyback
 * flow — the same inventory logic MyPacksPage runs, lifted into a hook so the
 * standalone `/shipping?with_navigation` page can reuse it without pulling in
 * the game-host wiring (reveal, buy-another, delivery seed). The three feeds
 * live at hook level so the lists survive tab switches and the delivery
 * sub-route round-trips.
 */
export function useShippingInventory(client: ApiClient) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const wallet = useWallet();

  const unopenedFetcher = useCallback(
    (page: number, per: number) =>
      getUnopenedPoolOffers(client, { page, per }).then((res) => ({
        items: res.offers,
        totalCount: res.totalCount,
      })),
    [client],
  );
  const inventoryFetcher = useCallback(
    (page: number, per: number) =>
      getInventory(client, { page, per }).then((res) => ({
        items: res.items,
        totalCount: res.totalCount,
      })),
    [client],
  );
  const shippingsFetcher = useCallback(
    (page: number, per: number) =>
      listPoolShippings(client, { page, per }).then((res) => ({
        items: res.shippings,
        totalCount: res.meta.totalCount,
      })),
    [client],
  );

  const repackz = useInfinitePagination<IframeUnopenedOffer>(unopenedFetcher, {
    per: PER_PAGE,
  });
  const drops = useInfinitePagination<IframePoolOrderItem>(inventoryFetcher, {
    per: PER_PAGE,
  });
  const deliveries = useInfinitePagination<IframePoolShipping>(shippingsFetcher, {
    per: PER_PAGE,
  });

  const selectedItems = useMemo(
    () => drops.items.filter((i) => selected.has(i.id)),
    [drops.items, selected],
  );

  const selectableIds = useMemo(
    () =>
      drops.items
        .filter((i) => (i.bestActiveOffer?.grossPrice ?? 0) > 0)
        .map((i) => i.id),
    [drops.items],
  );

  const handleItemToggle = useCallback((item: IframePoolOrderItem) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }, []);

  const refetchAfterSell = useCallback(() => {
    void drops.refetch();
  }, [drops]);

  // Per-item in-flight latch — blocks a fast double-tap on one card's Sell CTA
  // from firing two sellPoolItem POSTs (POSTs are never coalesced by the GET
  // dedup, so the guard lives here).
  const sellingItemIdsRef = useRef(new Set<IframePoolOrderItem['id']>());

  const handleItemSell = useCallback(
    async (item: IframePoolOrderItem) => {
      if (!item.poolOrderId) return;
      if (sellingItemIdsRef.current.has(item.id)) return;
      sellingItemIdsRef.current.add(item.id);
      // Snapshot the gross before selling — the backend nulls the offer out.
      const gross = item.bestActiveOffer?.grossPrice ?? 0;
      try {
        await sellPoolItem(client, item.poolOrderId, item.id);
        notifyBuyback(gross);
        void wallet.refresh({ expectCredit: true });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to sell card');
      } finally {
        sellingItemIdsRef.current.delete(item.id);
      }
      // Refetch on failure too: a card that can no longer be sold (e.g. it is
      // committed to a delivery) must drop its stale buyback pill.
      refetchAfterSell();
    },
    [client, refetchAfterSell, wallet],
  );

  const handleSubmit = useCallback(async () => {
    if (submitting || selected.size === 0) return;

    // Snapshot each card's gross BEFORE selling — the backend nulls out
    // best_active_offer once a card is sold, so the payout can't be read after
    // the refetch. Keyed by id so we sum only the cards that actually sell.
    const grossById = new Map(
      drops.items.map((i) => [i.id, i.bestActiveOffer?.grossPrice ?? 0]),
    );

    // Select All only marks the loaded page, so an explicit id list would skip
    // un-paginated cards. When the selection matches the loaded selectable set,
    // switch to `{ all: true }` so the backend sells the whole inventory.
    const allSelectable =
      selectableIds.length > 0 && selected.size === selectableIds.length;

    setSubmitting(true);
    try {
      const result = await sellInventoryItems(
        client,
        allSelectable ? { all: true } : { poolItemIds: Array.from(selected) },
      );

      if (result.sold.length > 0) {
        const payout = result.sold.reduce(
          (sum, item) => sum + (grossById.get(item.id) ?? 0),
          0,
        );
        notifyBuyback(payout);
      }
      if (result.failed.length > 0) {
        const failedLabel = result.failed.length === 1 ? 'card' : 'cards';
        toast.error(`${result.failed.length} ${failedLabel} couldn't be sold`);
      }

      setSelected(new Set());
      void wallet.refresh({ expectCredit: result.sold.length > 0 });
      refetchAfterSell();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to sell cards');
    } finally {
      setSubmitting(false);
    }
  }, [client, drops.items, refetchAfterSell, selectableIds, selected, submitting, wallet]);

  return {
    repackz,
    drops,
    deliveries,
    selected,
    setSelected,
    selectedItems,
    selectableIds,
    submitting,
    handleItemToggle,
    handleItemSell,
    handleSubmit,
    refetchAfterSell,
  };
}
